import type { TFunction } from 'i18next'
import { formatMoney, type MoneyCommunity } from '@/lib/money'
import type { ClassifiedsModuleConfig } from '@/types/space-wizard'
import type { ClassifiedAd, ClassifiedAdImage, ClassifiedAdStatus, ClassifiedSpaceInput } from '@/types/classifieds'

/** The limits the upload route enforces (§6): the form checks them first so the person is told before sending. */
export const MAX_IMAGES = 6
export const MAX_IMAGE_BYTES = 50 * 1024 * 1024
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

export const MAX_CATEGORIES = 50
export const CATEGORY_NAME_MAX = 60
export const PRESET_DURATIONS = [7, 15, 30, 60, 90]

/** The suggestions the wizard starts from (editable); the names are text the person reads, so they come from i18n. */
export const defaultClassifiedsConfig = (t: TFunction): ClassifiedsModuleConfig => ({
  categories: [
    { name: t('classifieds:config.suggestions.jobs') },
    { name: t('classifieds:config.suggestions.services') },
    { name: t('classifieds:config.suggestions.sales') }
  ],
  allowedDurations: [7, 15, 30],
  showValue: true,
  requireApproval: false
})

export interface ClassifiedsConfigErrors {
  categories?: string
  durations?: string
}

/**
 * Validation of the terms, shared by the wizard and the space edit (PADROES-UI §12):
 * at least one category and one term, each term from 1 to 365 days, no blank or repeated name.
 */
export const validateClassifiedsConfig = (
  config: ClassifiedsModuleConfig,
  t: TFunction
): ClassifiedsConfigErrors | null => {
  const errors: ClassifiedsConfigErrors = {}
  const names = (config.categories ?? []).map((c) => c.name.trim().toLowerCase())
  if (names.length === 0 || names.every((n) => !n)) errors.categories = t('classifieds:config.errors.categoriesRequired')
  else if (names.some((n) => !n)) errors.categories = t('classifieds:config.errors.categoryBlank')
  else if (new Set(names).size !== names.length) errors.categories = t('classifieds:config.errors.categoryRepeated')

  const durations = config.allowedDurations ?? []
  if (durations.length === 0) errors.durations = t('classifieds:config.errors.durationsRequired')
  else if (durations.some((d) => !Number.isInteger(d) || d < 1 || d > 365)) {
    errors.durations = t('classifieds:config.errors.durationRange')
  }
  return Object.keys(errors).length > 0 ? errors : null
}

/** What `PUT /classifieds/spaces/:id` takes: the whole ordered list, ids kept so a rename keeps its ads. */
export const toSpaceInput = (config: ClassifiedsModuleConfig): ClassifiedSpaceInput => ({
  allowedDurations: [...(config.allowedDurations ?? [])].sort((a, b) => a - b),
  showValue: config.showValue ?? true,
  requireApproval: config.requireApproval ?? false,
  categories: (config.categories ?? [])
    .map((c) => ({ ...(c.id ? { id: c.id } : {}), name: c.name.trim() }))
    .filter((c) => c.name)
})

/** The `errors` array of a refused request, by field. */
type ApiErrorLike = { message?: string; response?: { data?: { message?: string; errors?: unknown } } }

export const apiFieldErrors = (err: unknown): Record<string, string> => {
  const errors = (err as ApiErrorLike)?.response?.data?.errors
  const list: Array<{ param?: string; message?: string }> = Array.isArray(errors) ? errors : []
  const byField: Record<string, string> = {}
  for (const e of list) if (e.param && e.message && !byField[e.param]) byField[e.param] = e.message
  return byField
}

export const apiMessage = (err: unknown, fallback: string): string =>
  (err as ApiErrorLike)?.response?.data?.message || (err as ApiErrorLike)?.message || fallback

/**
 * "dd/mm" for the locale, in the community's time zone: the instant is the same
 * everywhere, but the day the term ends is the community's day (§5.4).
 */
export const formatShortDay = (iso: string | null | undefined, locale: string, timeZone?: string): string => {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', timeZone: timeZone || undefined }).format(new Date(iso))
  } catch {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(new Date(iso))
  }
}

export const formatLongDay = (iso: string | null | undefined, locale: string, timeZone?: string): string => {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric', timeZone: timeZone || undefined }).format(new Date(iso))
  } catch {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(iso))
  }
}

/**
 * Whole days left on the term (0 on the last day), counted on the community's
 * calendar. `null` when the ad has no term yet (under review).
 */
export const daysLeft = (iso: string | null | undefined, timeZone?: string, now = new Date()): number | null => {
  if (!iso) return null
  const dayKey = (d: Date) => {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone || undefined }).format(d)
    } catch {
      return new Intl.DateTimeFormat('en-CA').format(d)
    }
  }
  const toUtc = (key: string) => {
    const [y, m, d] = key.split('-').map(Number)
    return Date.UTC(y, m - 1, d)
  }
  return Math.round((toUtc(dayKey(new Date(iso))) - toUtc(dayKey(now))) / 86400000)
}

/** The value line: money in the community's format (never a hardcoded symbol), plus the note. `null` when the ad has no value. */
export const formatAdValue = (
  ad: Pick<ClassifiedAd, 'valueAmount' | 'valueCurrency' | 'valueNote'>,
  community: MoneyCommunity | null | undefined,
  locale: string
): string | null => {
  const hasAmount = ad.valueAmount !== null && ad.valueAmount !== undefined
  if (!hasAmount && !ad.valueNote) return null
  const parts: string[] = []
  if (hasAmount) parts.push(formatMoney(Number(ad.valueAmount), ad.valueCurrency, locale, community))
  if (ad.valueNote) parts.push(ad.valueNote)
  return parts.join(' · ')
}

export const STATUS_VARIANT: Record<ClassifiedAdStatus, 'success' | 'warning' | 'danger' | 'default'> = {
  active: 'success',
  pending: 'warning',
  rejected: 'danger',
  expired: 'default'
}

/** A hosted image the server is still resizing (its signed link is `null` until then). */
export const isImageProcessing = (image: Pick<ClassifiedAdImage, 'mediaId' | 'processingStatus'>) =>
  !!image.mediaId && (image.processingStatus === 'queued' || image.processingStatus === 'processing')

export const hasProcessingImages = (ad: Pick<ClassifiedAd, 'images'> | null | undefined) =>
  !!ad?.images?.some(isImageProcessing)

/** The ad's cover: the thumbnail on cards, the original on the detail. */
export const coverOf = (ad: Pick<ClassifiedAd, 'images'>, original = false): string | null => {
  const first = ad.images?.[0]
  if (!first) return null
  return original ? first.imageUrl : first.thumbnailUrl || first.imageUrl
}

/** Where an ad lives: `/{section}/{space}/{adId}`. */
export const adPath = (spacePath: string, adId: string, view?: 'interests') =>
  `${spacePath}/${adId}${view ? `?view=${view}` : ''}`

/** Plain text of the editor's HTML, to tell an empty description from `<p></p>`. */
export const htmlToText = (html: string): string => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
